import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../interceptors";
import EmptyState from "../../components/shared/EmptyState";
import LoadingState from "../../components/shared/LoadingState";
import ErrorState from "../../components/shared/ErrorState";

// ── /candidate/interviews — list of the candidate's interview rows ────────
// Backend: POST /job-posting/candidate-interviews/get returns the joined
// view (job, status, validity window, business name). One row per
// interview-invite.
//
// Filter chips bucket the rows into: All / Pending / In progress / Completed.
// Each card click navigates to the legacy /candidate/interview/:jobId
// detail page (the spec's /candidate/interviews/:applicationId page with
// the AI feedback report lands once the backend feedback generator is in).

const STATUS_TONE = {
  PENDING:     { label: "Pending",     pill: "bg-amber-100 text-amber-800" },
  IN_PROGRESS: { label: "In progress", pill: "bg-vf-tint text-vf-base" },
  ACCEPTED:    { label: "Accepted",    pill: "bg-vf-tint text-vf-base" },
  COMPLETED:   { label: "Completed",   pill: "bg-emerald-100 text-emerald-800" },
  SCORED:      { label: "Results",     pill: "bg-emerald-100 text-emerald-800" },
  EXPIRED:     { label: "Expired",     pill: "bg-slate-200 text-slate-700" },
};

function toneOf(status) {
  return STATUS_TONE[String(status || "").toUpperCase()] || STATUS_TONE.PENDING;
}

function fmtDateRange(start, end) {
  const s = start ? new Date(start) : null;
  const e = end ? new Date(end) : null;
  if (s && e) {
    const sameDay = s.toDateString() === e.toDateString();
    if (sameDay) return s.toLocaleDateString();
    return `${s.toLocaleDateString()} → ${e.toLocaleDateString()}`;
  }
  if (s) return `from ${s.toLocaleDateString()}`;
  if (e) return `by ${e.toLocaleDateString()}`;
  return "";
}

const FILTERS = [
  { key: "ALL",       label: "All" },
  { key: "PENDING",   label: "Pending" },
  { key: "ACTIVE",    label: "In progress" },
  { key: "COMPLETED", label: "Completed" },
];

function bucketOf(status) {
  const s = String(status || "").toUpperCase();
  if (s === "COMPLETED" || s === "SCORED") return "COMPLETED";
  if (s === "IN_PROGRESS" || s === "ACCEPTED") return "ACTIVE";
  return "PENDING";
}

export default function CandidateInterviews() {
  const candidateId = useMemo(() => {
    const raw = localStorage.getItem("candidateId") || localStorage.getItem("userId");
    return raw ? Number(raw) : null;
  }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [filter, setFilter] = useState("ALL");

  const load = () => {
    if (!candidateId) {
      setLoading(false);
      setError("Not signed in as a candidate");
      return;
    }
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    axiosInstance
      .post("/job-posting/candidate-interviews/get", { userId: String(candidateId) })
      .then((r) => {
        if (r.data?.status) {
          // Endpoint returns combined rows under `interviewDetails`.
          const list = Array.isArray(r.data.interviewDetails) ? r.data.interviewDetails : [];
          setRows(list);
        } else {
          setError(r.data?.message || "Couldn't load interviews");
          setErrorStatus(500);
        }
      })
      .catch((e) => {
        setError(e?.message || "Couldn't load interviews");
        setErrorStatus(e?.status || e?.response?.status || 500);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    document.title = "My interviews - Ample Hire";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  const counts = useMemo(() => {
    const c = { ALL: rows.length, PENDING: 0, ACTIVE: 0, COMPLETED: 0 };
    rows.forEach((r) => { c[bucketOf(r.interviewStatus)] += 1; });
    return c;
  }, [rows]);

  const visible = useMemo(() => {
    if (filter === "ALL") return rows;
    return rows.filter((r) => bucketOf(r.interviewStatus) === filter);
  }, [rows, filter]);

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-vf-base m-0">My interviews</h1>
        <p className="text-sm text-vf-muted mt-1">
          Every interview invitation in one place. Click a row to start, resume, or review.
        </p>
      </header>

      {/* Filter chips */}
      <div
        role="tablist"
        aria-label="Filter interviews by status"
        className="flex flex-wrap gap-2"
      >
        {FILTERS.map((f) => {
          const active = filter === f.key;
          const n = counts[f.key] ?? 0;
          return (
            <button
              key={f.key}
              role="tab"
              type="button"
              aria-selected={active}
              onClick={() => setFilter(f.key)}
              className={`text-xs px-3 py-1.5 rounded-full border min-h-[36px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary ${
                active
                  ? "bg-vf-primary text-white border-vf-primary"
                  : "bg-white text-vf-base border-subtle hover:bg-vf-tint"
              }`}
            >
              {f.label}
              <span className={`ml-1.5 ${active ? "text-white/80" : "text-vf-muted"}`}>{n}</span>
            </button>
          );
        })}
      </div>

      {/* Body */}
      {loading ? (
        <div className="bg-white border border-subtle rounded-card p-4">
          <LoadingState variant="skeleton" shape="row" count={4} />
        </div>
      ) : error ? (
        <ErrorState
          status={errorStatus}
          description={errorStatus === 403 ? undefined : error}
          retry={load}
        />
      ) : visible.length === 0 ? (
        <div className="bg-white border border-subtle rounded-card">
          <EmptyState
            icon={<span aria-hidden="true">📋</span>}
            title={
              filter === "ALL"
                ? "No interviews yet"
                : `No ${FILTERS.find((f) => f.key === filter)?.label?.toLowerCase() || "matching"} interviews`
            }
            description={
              filter === "ALL"
                ? "When a recruiter sends you an interview invitation, it'll appear here."
                : "Try another filter or check back later."
            }
          />
        </div>
      ) : (
        <ul className="list-none p-0 m-0 space-y-2.5">
          {visible.map((it) => {
            const tone = toneOf(it.interviewStatus);
            const bucket = bucketOf(it.interviewStatus);
            const validity = fmtDateRange(it.startValidityTime, it.endValidityTime);
            // Completed interviews route to the feedback report
            // (interviewId-keyed); pending/in-progress route to the
            // legacy in-flight interview page (jobId-keyed).
            const linkTo =
              bucket === "COMPLETED" && it.interviewId
                ? `/candidate/interviews/${it.interviewId}`
                : `/candidate/interview/${it.jobId}`;
            return (
              <li key={`${it.interviewId}-${it.jobId}`}>
                <Link
                  to={linkTo}
                  className="block rounded-card border border-subtle bg-white px-4 py-3 hover:border-vf-primary/40 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-vf-base truncate">
                        {it.positionName || `Job #${it.jobId}`}
                      </div>
                      <div className="text-xs text-vf-muted mt-0.5 truncate">
                        {it.businessName ? <>{it.businessName} · </> : null}
                        {it.interviewRounds || "Interview round"}
                        {validity ? <> · {validity}</> : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`text-2xs uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold ${tone.pill}`}>
                        {tone.label}
                      </span>
                      <span className="text-xs font-semibold text-vf-primary">
                        {bucket === "PENDING" ? "Start →" : bucket === "ACTIVE" ? "Resume →" : "Review →"}
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
